import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { CreatePaymentInput, PaymentMethod } from '@/types/domain/payment-management';
import { InvoiceRecord } from '@/types/domain/invoice-management';
import { mockInvoiceService } from '@/services/mock/MockInvoiceService';
import { formatCurrency } from '@/lib/utils';
import {
  CreditCard,
  X,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Building,
  DollarSign,
} from 'lucide-react';
import { validatePaymentAmount } from '@/features/payments/utils/paymentCalculations';

interface PaymentExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (input: CreatePaymentInput, idempotencyKey: string) => Promise<void>;
  preselectedInvoice?: InvoiceRecord | null;
}

export const PaymentExecutionModal: React.FC<PaymentExecutionModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  preselectedInvoice,
}) => {
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRecord | null>(preselectedInvoice || null);
  const [amount, setAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('NEFT');
  const [scheduledDate, setScheduledDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState<string>('');
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (preselectedInvoice) {
      setSelectedInvoice(preselectedInvoice);
      setAmount(preselectedInvoice.totalAmount);
    }
  }, [preselectedInvoice]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const loadInvoices = async () => {
        try {
          setIsLoadingInvoices(true);
          const res = await mockInvoiceService.getInvoices({
            search: '',
            status: 'ALL',
            matchingStatus: 'ALL',
            paymentStatus: 'ALL',
            exceptionCode: 'ALL',
            plant: 'ALL',
            vendor: 'ALL',
            page: 1,
            pageSize: 50,
          });
          setInvoices(res.items);
          if (!preselectedInvoice && res.items.length > 0) {
            setSelectedInvoice(res.items[0]!);
            setAmount(res.items[0]!.totalAmount);
          }
        } finally {
          setIsLoadingInvoices(false);
        }
      };
      loadInvoices();
    } else {
      document.body.style.overflow = '';
      setErrorMessage(null);
    }
  }, [isOpen, preselectedInvoice]);

  if (!isOpen) return null;

  const handleSelectInvoice = (inv: InvoiceRecord) => {
    setSelectedInvoice(inv);
    setAmount(inv.totalAmount);
    setErrorMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) {
      setErrorMessage('Please select an invoice for payment disbursement.');
      return;
    }

    const val = validatePaymentAmount(amount, selectedInvoice.totalAmount);
    if (!val.isValid) {
      setErrorMessage(val.errorMessage || 'Invalid payment amount.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      const idempotencyKey = `pay-tx-${selectedInvoice.id}-${amount}-${Date.now()}`;
      await onCreated(
        {
          invoiceId: selectedInvoice.id,
          amount,
          paymentMethod,
          scheduledDate,
          notes,
        },
        idempotencyKey
      );
      onClose();
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to create payment instruction.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-execution-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-xl overflow-hidden font-heading flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 id="payment-execution-modal-title" className="text-lg font-bold text-foreground">
                Issue Electronic Disbursement
              </h2>
              <p className="text-xs text-foreground-muted">
                Simulate electronic treasury transfer (NEFT/RTGS/Wire) cleared via SAP F110.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-foreground-muted hover:text-foreground rounded-lg transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="mx-6 mt-4 p-3.5 bg-rose-500/10 border border-rose-500/30 rounded-xl flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <p className="text-xs font-semibold text-rose-700 dark:text-rose-400">{errorMessage}</p>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5 flex-1">
          {/* Invoice Selection */}
          <div>
            <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
              Target Approved Invoice
            </label>
            {isLoadingInvoices ? (
              <div className="flex items-center gap-2 p-3 rounded-xl border border-border text-xs text-foreground-muted">
                <RefreshCw className="h-4 w-4 animate-spin" /> Loading payable invoices...
              </div>
            ) : (
              <select
                aria-label="Select Target Approved Invoice"
                value={selectedInvoice?.id || ''}
                onChange={(e) => {
                  const found = invoices.find((inv) => inv.id === e.target.value);
                  if (found) handleSelectInvoice(found);
                }}
                className="w-full px-3.5 py-2.5 bg-background border border-border rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary focus:outline-hidden"
              >
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} — {inv.vendorName} ({formatCurrency(inv.totalAmount, inv.currency)})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Selected Invoice Details Card */}
          {selectedInvoice && (
            <div className="p-3.5 bg-muted/40 rounded-xl border border-border space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-foreground">{selectedInvoice.vendorName}</span>
                <span className="font-mono text-purple-600 font-bold">{selectedInvoice.invoiceNumber}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1 border-t border-border/60 text-[11px]">
                <div>
                  <span className="text-foreground-muted block">PO Commitment</span>
                  <span className="font-bold text-foreground">{selectedInvoice.poNumber}</span>
                </div>
                <div>
                  <span className="text-foreground-muted block">Receiving Evidence</span>
                  <span className="font-bold text-foreground">{selectedInvoice.grnNumber || 'No GRN'}</span>
                </div>
                <div>
                  <span className="text-foreground-muted block">Total Payable</span>
                  <span className="font-bold text-emerald-600">
                    {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Amount & Settlement Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
                Disbursement Amount ({selectedInvoice?.currency || 'INR'})
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted text-xs font-bold">
                  <DollarSign className="h-3.5 w-3.5" />
                </span>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={amount || ''}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  className="w-full pl-9 pr-3.5 py-2.5 bg-background border border-border rounded-xl text-xs font-bold text-foreground focus:ring-2 focus:ring-primary focus:outline-hidden"
                />
              </div>
              {selectedInvoice && (
                <div className="flex gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setAmount(selectedInvoice.totalAmount)}
                    className="text-[10px] font-bold text-primary hover:underline cursor-pointer"
                  >
                    100% Full Settlement
                  </button>
                  <span className="text-[10px] text-foreground-muted">|</span>
                  <button
                    type="button"
                    onClick={() => setAmount(Math.round(selectedInvoice.totalAmount * 0.5 * 100) / 100)}
                    className="text-[10px] font-bold text-primary hover:underline cursor-pointer"
                  >
                    50% Partial Settlement
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
                Payment Clearing Method
              </label>
              <select
                aria-label="Payment Clearing Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                className="w-full px-3.5 py-2.5 bg-background border border-border rounded-xl text-xs font-medium focus:ring-2 focus:ring-primary focus:outline-hidden"
              >
                <option value="NEFT">NEFT (Electronic Funds Transfer)</option>
                <option value="RTGS">RTGS (Real Time Gross Settlement)</option>
                <option value="WIRE_TRANSFER">Commercial Wire Transfer</option>
                <option value="ACH">ACH Automated Clearing</option>
                <option value="CHECK">Corporate Check</option>
              </select>
            </div>
          </div>

          {/* Scheduled Date */}
          <div>
            <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
              Scheduled Disbursement Execution Date
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted text-xs">
                <Calendar className="h-4 w-4" />
              </span>
              <input
                type="date"
                required
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full pl-10 pr-3.5 py-2 bg-background border border-border rounded-xl text-xs font-medium text-foreground focus:ring-2 focus:ring-primary focus:outline-hidden"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-bold text-foreground-secondary mb-1.5 block">
              Treasury Instruction Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Standard settlement run cleared per agreed PO payment terms..."
              className="w-full px-3.5 py-2 bg-background border border-border rounded-xl text-xs focus:ring-2 focus:ring-primary focus:outline-hidden resize-none"
            />
          </div>

          {/* Simulation Notice */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-2.5 text-[11px] text-blue-700 dark:text-blue-400">
            <Building className="h-4 w-4 shrink-0" />
            <span>
              <strong>SIMULATED TREASURY TRANSACTION:</strong> This action issues a simulated payment
              instruction into the central procurement mock database.
            </span>
          </div>

          {/* Footer Buttons */}
          <div className="pt-4 border-t border-border flex items-center justify-end gap-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isSubmitting || !selectedInvoice}>
              {isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Disbursing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Authorize & Disburse
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
